/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

export const FILTERS_QUERY_PARAM = 'filters';
export const FILTERS_OPEN = 'open';
export const FILTERS_CLOSED = 'closed';

export function getInitialFiltersOpen(searchParams: URLSearchParams): boolean {
    return searchParams.get(FILTERS_QUERY_PARAM) === FILTERS_OPEN;
}

export const ACTION_PARAMS = ['action', 'actionParams'] as const;

function stripParams(search: string, keys: readonly string[]): string {
    const params = new URLSearchParams(search);
    for (const key of keys) {
        params.delete(key);
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

export function getSearchWithoutFiltersParam(search: string): string {
    return stripParams(search, [FILTERS_QUERY_PARAM]);
}

/** Strips pending action params (action, actionParams) while preserving search state. */
export function getSearchWithoutActionParams(search: string): string {
    return stripParams(search, ACTION_PARAMS);
}

/** Strips all client-only params (filters, pending action) for shouldRevalidate comparison. */
export function getSearchWithoutClientOnlyParams(search: string): string {
    return stripParams(search, [FILTERS_QUERY_PARAM, ...ACTION_PARAMS]);
}

function buildSearchWithFiltersState(search: string, isOpen: boolean): string {
    const params = new URLSearchParams(search);
    params.set(FILTERS_QUERY_PARAM, isOpen ? FILTERS_OPEN : FILTERS_CLOSED);
    return `?${params.toString()}`;
}

export function useFiltersPanelState(initialFiltersOpen?: boolean) {
    const location = useLocation();
    const navigate = useNavigate();
    const [filtersOpen, setFiltersOpen] = useState(Boolean(initialFiltersOpen));

    // Keep local state synchronized when user navigates via back/forward and query param changes.
    useEffect(() => {
        const nextFiltersOpen = getInitialFiltersOpen(new URLSearchParams(location.search));
        setFiltersOpen(nextFiltersOpen);
    }, [location.search]);

    /**
     * Records the shopper's explicit choice in the URL.
     *
     * `next` is OPTIONAL but callers with a resolved state must pass it. Since CRCB2B-348 the
     * panel has three states — `open`, `closed`, and absent-means-the-breakpoint-decides — so
     * `filtersOpen` (which is `false` for BOTH `closed` and absent) no longer describes what is
     * on screen. Deriving the next value from it at desktop, where absent renders OPEN, produced
     * a first click that wrote `filters=open` over an already-open sidebar and changed nothing.
     * Pass `!filtersVisible` from `useFiltersVisible` instead — see `use-panel-presentation`.
     *
     * The `typeof` guard is deliberate: this is wired to `onClick` in two routes, and a bare
     * `onClick={toggleFiltersOpen}` would hand it a MouseEvent, which is truthy.
     */
    const toggleFiltersOpen = useCallback(
        (next?: boolean) => {
            const nextFiltersOpen = typeof next === 'boolean' ? next : !filtersOpen;
            setFiltersOpen(nextFiltersOpen);
            void navigate(
                {
                    pathname: location.pathname,
                    search: buildSearchWithFiltersState(location.search, nextFiltersOpen),
                },
                // `preventScrollReset` is required, not a nicety. This is a real navigation — it
                // exists only to record the panel state in the URL — and React Router scrolls to
                // the top on every navigation when <ScrollRestoration> is mounted (root.tsx).
                // `replace: true` does NOT exempt it: replace controls the history entry, not the
                // scroll position. Without this, opening or closing the panel threw a shopper who
                // had scrolled down the listing back to the top of the page (CRCB2B-87).
                //
                // Same reasoning as `swatch-group/swatch.tsx`, which passes it for the same reason.
                { replace: true, preventScrollReset: true }
            );
        },
        [filtersOpen, location.pathname, location.search, navigate]
    );

    return [filtersOpen, toggleFiltersOpen] as const;
}
